import React from 'react';
import { PaperWarning, DocumentTitle } from './MissingDocument.styles';
import TrackerAnchor from '../Tracker/TrackerAnchor';
import { LinkWithNativeEvents } from '@components/_common/link/link';

type DocumentLinkProps = {
  href: string;
  trackEventable?: () => void;
  extraData: [string, string];
  children: React.ReactNode;
  additionalText: string;
};

const DocumentLink: React.FC<DocumentLinkProps> = ({
  href,
  trackEventable = () => {},
  extraData,
  children,
  additionalText,
}) => {
  return (
    <>
      <LinkWithNativeEvents
        href={href}
        target="_blank"
        nativeEvent={{
          event: 'link',
          payload: {
            url: href,
          },
        }}
        as={TrackerAnchor as any}
        trackEventable={trackEventable}
        extraData={extraData}
      >
        {children}
      </LinkWithNativeEvents>
      {additionalText}
    </>
  );
};

type MissingDocumentProps = {
  documentName: string;
};

const MissingDocument = ({ documentName }: MissingDocumentProps) => {
  const getDocumentsWithFaq = (docName: string, trackEventable = () => {}) => {
    switch (docName) {
      case 'Current Insurance for recently purchased vehicle':
        return (
          <PaperWarning>
            <DocumentLink
              href="/help/purchasing-a-car/what-is-proof-of-insurance-and-how-does-it-affect-registration"
              trackEventable={trackEventable}
              extraData={['Proof of Insurance', 'FAQ']}
              additionalText=" about insurance requirements and how to finish you registration"
            >
              Learn more
            </DocumentLink>
          </PaperWarning>
        );
      case 'Generic Limited Power of Attorney':
        return (
          <PaperWarning>
            <DocumentLink
              href="/help/purchasing-a-car/what-is-the-limited-power-of-attorney-and-how-do-i-provide-it"
              trackEventable={trackEventable}
              extraData={['Generic Limited Power of Attorney', 'FAQ']}
              additionalText=" about this document and how to finish your registration. For customers in AL, AZ, OH, NJ, or SC, please mail in your document with the FedEx return label"
            >
              Learn More
            </DocumentLink>
          </PaperWarning>
        );
      case 'Generic Odometer Disclosure Statement':
        return (
          <PaperWarning>
            <DocumentLink
              href="/help/purchasing-a-car/what-is-the-odometer-disclosure-and-how-do-i-provide-it"
              trackEventable={trackEventable}
              extraData={['Generic Odometer Disclosure Statement', 'FAQ']}
              additionalText=" about this document and how to finish your registration. Please sign and mail this document back to Carvana using the FedEx return label provided."
            >
              Learn More
            </DocumentLink>
          </PaperWarning>
        );
      case 'Misc Registration Paperwork':
        return (
          <PaperWarning>
            <DocumentLink
              href="/help/purchasing-a-car/why-is-carvana-asking-for-my-registration-paperwork"
              trackEventable={trackEventable}
              extraData={['Registration Paperwork', 'FAQ']}
              additionalText=" about this document and how to finish your registration. Please mail this document back to Carvana using the FedEx return label provided."
            >
              Learn More
            </DocumentLink>
          </PaperWarning>
        );
      case '(South Carolina) Paid property tax receipt':
        return (
          <PaperWarning>
            <DocumentLink
              href="/help/purchasing-a-car/why-is-the-south-carolina-paid-property-tax-receipt-how-does-it-affect-registration"
              trackEventable={trackEventable}
              extraData={['South Carolina paid property tax receipt', 'FAQ']}
              additionalText=" about the South Carolina Paid Property Tax receipt."
            >
              Learn More
            </DocumentLink>
          </PaperWarning>
        );
      case '(Texas) Vehicle Inspection Report (VIR)':
        return (
          <PaperWarning>
            <DocumentLink
              href="/help/purchasing-a-car/what-is-the-texas-vehicle-inspection-report-and-how-do-i-provide-it-for-vehicle-registration"
              trackEventable={trackEventable}
              extraData={['TX Vehicle Inspection Report', 'FAQ']}
              additionalText=" about the Texas Vehicle Inspection Report. Please mail this document back to Carvana using the FedEx return label provided."
            >
              Learn more
            </DocumentLink>
          </PaperWarning>
        );
      case "Current Driver's License":
        return (
          <PaperWarning>
            <DocumentLink
              href="/help/purchasing-a-car/what-are-the-drivers-license-requirements-for-my-purchase-and-registration"
              trackEventable={trackEventable}
              extraData={["Driver's License", 'FAQ']}
              additionalText=" about what's required on your Driver's License and how to finish your registration."
            >
              Learn more
            </DocumentLink>
          </PaperWarning>
        );
      case 'Address Verification':
        return (
          <PaperWarning>
            <DocumentLink
              href="/help/purchasing-a-car/what-is-address-verification"
              trackEventable={trackEventable}
              extraData={['Address Verification', 'FAQ']}
              additionalText=" about Address Verification documents and how to finish your registration."
            >
              Learn more
            </DocumentLink>
          </PaperWarning>
        );
      default:
        return null;
    }
  };

  const getHelpText = (trackEventable = () => {}) => getDocumentsWithFaq(documentName, trackEventable);

  return (
    <div style={{ paddingBottom: '10px', marginLeft: '25px' }}>
      <DocumentTitle>
        <h4 className="missingDocumentTitle">{documentName}</h4>
        {getHelpText()}
      </DocumentTitle>
    </div>
  );
};

export default MissingDocument;
